import { useState } from 'react'

import type { User } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { useT } from '@/i18n'

import { useFollowToggle } from '../hooks/useProfile'

interface Props {
  user: User
  size?: 'sm' | 'md'
}

/**
 * 已关注时悬停显示"取消关注"并变红。
 *
 * 受保护的账号（D75、D76）：没关注时按钮是「申请关注」；申请过、还没被处理时是
 * 「已申请」，悬停变成「撤回申请」，点了走同一个 DELETE。
 */
export function FollowButton({ user, size = 'md' }: Props) {
  const t = useT()
  const toggle = useFollowToggle()
  const [hovering, setHovering] = useState(false)

  if (!user.viewer || user.viewer.is_self) return null
  // 存在屏蔽关系时关注一定失败，按钮不如不给
  if (user.viewer.blocked_by || user.viewer.blocking) return null

  const following = user.viewer.following
  const requested = !following && Boolean(user.viewer.follow_requested)
  const active = following || requested

  let label = t('follow.follow')
  if (following) label = hovering ? t('follow.unfollow') : t('follow.following')
  else if (requested) label = hovering ? t('follow.cancelRequest') : t('follow.requested')
  else if (user.is_private) label = t('follow.request')

  return (
    <Button
      size={size}
      variant={active ? (hovering ? 'danger' : 'outline') : 'secondary'}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onClick={(event) => {
        event.stopPropagation()
        event.preventDefault()
        toggle.mutate({ username: user.username, following: active, isPrivate: user.is_private })
      }}
      className="min-w-24"
    >
      {label}
    </Button>
  )
}
